import dynamic from 'next/dynamic';
import { signOut, useSession } from 'next-auth/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useConfirmedSession from '../hooks/useConfirmedSession';
import useIdentifyMetrics from '../hooks/useIdentifyMetrics';
import { useMetrics } from '../hooks/useMetrics';
import useSignOut from '../hooks/useSignOut';
import BackendService from '../services/backendService';
import { AUTH_EVENTS, clearAuthPending, readAuthPending } from '../utils/authFunnel';
import { isProduction } from '../utils/client';
import { getEndImpersonationCallbackUrl } from '../utils/endImpersonationRedirect';
import { buildForcedLogoutLoginUrl } from '../utils/navigator';
import { configureStore } from '../utils/store';
import { AnonymousIntercomAutoBoot } from './CustomIntercomStateProvider';

const ImpersonationBanner = dynamic(
  () => import('../components/_organisms/ImpersonationBanner/ImpersonationBanner'),
  { ssr: false },
);

const MetricsIdentifier = ({ uid }: { uid: string }) => {
  useIdentifyMetrics(uid);
  return null;
};

const BackendServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const session = useSession();
  const { signOutAuto } = useSignOut();
  const metrics = useMetrics(true);
  const { config } = useMemo(() => configureStore(), []);
  // Sticky uid/hasBeenAuthenticated survive a transient `/api/auth/session`
  // fetch failure (see useConfirmedSession's docblock) — `session.data?.user
  // ?.uid` alone does not, so branching on it here would mount
  // AnonymousIntercomAutoBoot for an already-gated user during that blip and
  // undo the country/plan shutdown (nothing else re-shuts it down, since
  // useIdentifyMetrics's effect isn't running while MetricsIdentifier is
  // unmounted).
  const { userId: confirmedUserId, hasBeenAuthenticated } = useConfirmedSession();

  useState(() => {
    new BackendService().init(config.backend.externalApiUrl, config.cdn.url);
    if (session && session.data?.idToken) {
      BackendService.instance.setToken(session.data.idToken as string);
    }
  });

  useEffect(() => {
    if (session && session.data?.idToken) {
      new BackendService();
      BackendService.instance.setToken(session.data.idToken as string);
    }
  }, [config.backend.externalApiUrl, config.cdn.url, session]);

  // One-shot guard so a session that keeps re-resolving with the same error
  // doesn't fire competing sign-outs.
  const recoveryStarted = useRef(false);

  useEffect(() => {
    const error = session?.data?.error;
    if (!error || recoveryStarted.current) return;

    // The auth pages own errored-session handling: their GSSP clears the cookie and
    // they render the form / error UI. Skip the global recovery there — otherwise a
    // briefly-cached errored client session would yank the user off /login (or
    // /signup, /auth/error) back to /auth/error and steal their retry.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path === '/login' || path === '/signup' || path === '/auth/error') return;

    // New-auth provisioning failed (exchange/self-join returned no session). Sign
    // out and land on the styled /auth/error page with the specific reason, rather
    // than a silent sign-out that leaves the user with no explanation.
    if (error === 'ProvisioningError') {
      recoveryStarted.current = true;
      const type = session?.data?.authErrorType ?? 'oauthCallbackError';
      const correlationId = session?.data?.correlationId;
      const url = new URL(`${window.location.origin}/auth/error`);
      url.searchParams.set('error_type', type);
      if (correlationId) url.searchParams.set('correlation_id', correlationId);
      // Preserve the non-prod QA override so staging testers who enabled the new
      // experience via ?newauth=1 still get AuthErrorView, not the legacy modal.
      if (!isProduction()) url.searchParams.set('newauth', '1');
      signOutAuto(url.toString());
      return;
    }

    if (error === 'RefreshAccessTokenError') {
      recoveryStarted.current = true;
      if (session?.data?.isImpersonation) {
        signOut({ callbackUrl: getEndImpersonationCallbackUrl() });
      } else {
        // Clear the NextAuth session and land on /login so the user can pick how
        // to sign in. A plain signIn() here would silently re-auth via SSO and
        // leave them in the same broken state. Preserve where the user was via
        // ?redirect= so login.tsx returns them there after re-auth.
        signOutAuto(buildForcedLogoutLoginUrl(window.location.origin));
      }
    }
  }, [session, signOutAuto]);

  // Signup funnel: close the loop once auth actually succeeds. Pairs the pending
  // marker (set at initiation, surviving the OAuth redirect) with isNewUser from
  // the resolved session, so Auth.Succeeded fires once per attempt — across every
  // flow — and distinguishes signups (incl. a login click that created an account).
  useEffect(() => {
    if (session.status !== 'authenticated') return;
    const data = session.data;
    if (!data?.idToken || data.error) return; // only a genuine, usable login
    const pending = readAuthPending();
    if (!pending) return;
    metrics.logEvent(AUTH_EVENTS.succeeded, {
      method: pending.method,
      provider: pending.provider,
      intendedMode: pending.mode,
      isNewUser: !!data.isNewUser,
    });
    clearAuthPending();
  }, [session, metrics]);

  return (
    <>
      {session?.data?.isImpersonation && <ImpersonationBanner />}
      {hasBeenAuthenticated && confirmedUserId ? (
        <MetricsIdentifier uid={confirmedUserId} />
      ) : (
        // Mutually exclusive with MetricsIdentifier, which owns booting (with
        // identity) once a session exists — see AnonymousIntercomAutoBoot's
        // docblock for why only one of the two may ever call `boot`. Gated on
        // the sticky `hasBeenAuthenticated`, not a raw uid check, so a
        // transient session blip can't flip this to the anonymous branch for
        // someone who was already signed in.
        <AnonymousIntercomAutoBoot />
      )}
      {children}
    </>
  );
};

export default BackendServiceProvider;
